// CTV-682: the catalogue of print-ready marketing materials a station can hand
// out. Every sheet is rendered from PublisherConfig, so onboarding a station
// gives it a full set of collateral with no design work.
//
// The Materials tab itself is visible to everyone on a station's team; what a
// given person sees *inside* it is decided here by `minRole`. This is a
// presentation gate only — these sheets carry no station-private data, so
// there's no API surface to guard alongside it (unlike Referrals, where the
// commission figures made server-side enforcement necessary).

import { rankOf } from '../shared/roleRank';
import type { MaterialsVideo } from '../../../types/selfService';
import { DISPLAY_AD_SPECS, type DisplayAdSize } from './displayAdSizes';

/** A material rendered from PublisherConfig by one of the page's sheet components. */
export type SheetMaterialId =
  | 'advertiser-onepager'
  | 'guide-onepager'
  | 'rep-onepager'
  // CTV-840: one web banner per size, downloaded as a PNG rather than printed.
  | 'display-ad-320x50'
  | 'display-ad-728x90'
  | 'display-ad-300x250'
  | 'display-ad-300x600'
  | 'display-ad-1080x1080'
  // CTV-856: the email signature graphic. Rendered like a display ad (a
  // DisplayAdSize under the hood) but its own kind of material to the reader.
  | 'email-signature'
  // The previous generation, kept in the picker so the new design can be
  // compared against what it replaces. The generation before that lives in
  // ./archive/v1 — still on disk, deliberately NOT in the catalogue, because
  // three generations in one picker is unreadable. Drop these three once the
  // new set is signed off.
  | 'advertiser-onepager-v2'
  | 'guide-onepager-v2'
  | 'rep-onepager-v2';

/**
 * CTV-863: a video is not in the catalogue. Each station has its own, kept
 * on the station row, and its id here is `video-<entry id>` (see
 * videoMaterial), so a link to one is as stable as a link to a sheet.
 */
export type MaterialId = SheetMaterialId | `video-${string}`;

/**
 * Picker grouping. Kept as a union rather than a free string so a typo can't
 * silently create a one-item group nobody notices.
 */
// CTV-856 (decided 2026-09-09): groups are named for the kind of artefact, so
// the signature gets its own group rather than a Miscellaneous drawer.
// CTV-863: Videos follows the same pattern.
export type MaterialCategory =
  | 'One-Pagers'
  | 'Display Ads'
  | 'Email Signature'
  | 'Videos'
  | 'Previous version';

/**
 * What each category is for, in the station's terms, for the Materials
 * landing page. One sentence: the cards under it carry the detail. Adding a
 * category without a blurb fails to typecheck, so the landing never shows a
 * bare heading.
 */
export const CATEGORY_BLURBS: Record<MaterialCategory, string> = {
  'One-Pagers': 'Print sheets to hand to advertisers and your own team.',
  'Display Ads': 'Web banners for your site, apps, and social accounts. Download as PNG.',
  'Email Signature':
    'A branded picture for the bottom of your emails, with your referral code. Download as PNG.',
  Videos: 'Videos made for your station, ready to send on or post. Download as MP4.',
  'Previous version':
    'The sheets these replaced, kept for comparison while the new set is reviewed.',
};

export interface MaterialDef {
  id: MaterialId;
  /** Shown in the material picker. Names the artefact, not its argument. */
  title: string;
  /**
   * Who it's handed to and what it's for, shown under the title. Takes the
   * station's displayed name so the picker reads in the station's own terms
   * ("how they get on KEZI") rather than generic ad-industry shorthand.
   */
  blurb: (stationName: string) => string;
  /** Collapsible section this material appears under in the picker. */
  category: MaterialCategory;
  /** Lowest role that may see this material. */
  minRole: 'sales_rep' | 'manager';
  /**
   * Trailing part of the download filename: One-Pager_<slug>_<fileSuffix>.
   * Chrome takes the PDF's default filename from document.title, so the page
   * swaps the title to this while the print dialog is open.
   */
  fileSuffix: string;
  /**
   * CTV-752: still being written. A draft is hidden from station teams so a
   * half-built sheet can land on dev without being handed out; super_admins
   * still see it, badged as a draft. Flip this off when the sheet is finished.
   */
  draft?: boolean;
  /**
   * CTV-840: set on display ads only. The material is a web image at this
   * size, downloaded as a PNG instead of printed, and the page picks the
   * download action over the print one by this field's presence.
   */
  displayAd?: DisplayAdSize;
  /**
   * CTV-863: set on videos only. The material is this station's own mp4,
   * watched and downloaded as it is; nothing about it is rendered from
   * PublisherConfig, so it has no sheet component.
   */
  video?: MaterialsVideo;
}

/** CTV-863: who may add, rename, or delete a video: the roles the API admits. */
const VIDEO_EDITOR_ROLES: ReadonlyArray<string> = ['super_admin', 'organization_admin', 'manager'];
export const canEditVideos = (role: string | null | undefined): boolean =>
  VIDEO_EDITOR_ROLES.includes(role ?? '');

/**
 * CTV-863: the picker entry for one of the station's videos. Open to reps:
 * the video is made to be handed on. The blurb is the uploader's own
 * description (feedback 2026-09-10: a generic line under every video said
 * nothing), so it is empty when none was given.
 */
export function videoMaterial(video: MaterialsVideo): MaterialDef {
  return {
    id: `video-${video.id}`,
    category: 'Videos',
    title: video.name,
    blurb: () => video.description,
    minRole: 'sales_rep',
    fileSuffix: fileSlug(video.name),
    video,
  };
}

/**
 * The picker entry for one banner size. The blurb is the size's industry
 * name, since a station's web team thinks in placements, not pixel counts
 * (feedback 2026-09-04: the line about where it goes was cut). It does not
 * repeat that the ad is branded and downloadable: the page subtitle says so
 * once, and five cards saying it again buried the one line that differs.
 */
function displayAd(size: DisplayAdSize): MaterialDef {
  const { width, height, name, signature } = DISPLAY_AD_SPECS[size];
  // CTV-856: the signature graphic is named for where it goes, not its
  // pixels; a rep looking for it does not think "480x96". Its filename is
  // `Email-Signature_<slug>`: the suffix leads (see materialFileName).
  if (signature) {
    return {
      id: 'email-signature',
      category: 'Email Signature',
      title: name,
      blurb: () => 'For the bottom of your emails. Carries your referral code and links to your storefront.',
      minRole: 'sales_rep',
      fileSuffix: 'Email-Signature',
      displayAd: size,
    };
  }
  return {
    // The signature returned above, so what is left is a web size.
    id: `display-ad-${size as Exclude<DisplayAdSize, '480x96'>}`,
    category: 'Display Ads',
    title: `Display Ad ${width}×${height}`,
    blurb: () => `${name}.`,
    minRole: 'sales_rep',
    fileSuffix: size,
    displayAd: size,
    // CTV-840: signed off 2026-09-08; station teams see the whole set.
  };
}

export const MATERIALS: MaterialDef[] = [
  {
    id: 'advertiser-onepager',
    category: 'One-Pagers',
    title: 'One-Pager for Advertisers',
    blurb: (station) =>
      `Leave with a business you are pitching. Explains streaming TV, what it costs, and how they get on ${station}.`,
    minRole: 'sales_rep',
    fileSuffix: 'Advertisers',
    // Signed off Aug 2026 — the only sheet station teams can see. The other
    // two are still on the pre-revision design and stay draft.
  },
  {
    id: 'guide-onepager',
    category: 'One-Pagers',
    title: 'One-Pager for Getting Started',
    blurb: (station) =>
      `Walks an advertiser through what to have ready and what happens after they pay. Hand over once they decide to run on ${station}.`,
    minRole: 'sales_rep',
    fileSuffix: 'Getting-Started',
    // CTV-752: under revision; hidden from station teams until re-approved.
    draft: true,
  },
  {
    id: 'rep-onepager',
    category: 'One-Pagers',
    title: 'One-Pager for Sales Reps',
    blurb: () =>
      'Explains how self-serve benefits your sales reps. Covers referral links, commission, and what stays theirs.',
    // Manager+ only. This sheet is a manager's script for a conversation with
    // their own team; handing a rep the sheet meant to reassure them lands
    // badly out of context.
    minRole: 'manager',
    fileSuffix: 'Reps',
    // CTV-752: under revision; hidden from station teams until re-approved.
    draft: true,
  },

  // --- CTV-840: web banners for the station's own site and social ----------
  displayAd('300x250'),
  displayAd('728x90'),
  displayAd('320x50'),
  displayAd('300x600'),
  displayAd('1080x1080'),

  // --- CTV-856: the email signature graphic, its own group ------------------
  displayAd('480x96'),

  // --- the previous generation, for side-by-side comparison only ------------
  // Same minRole as their replacements so a reviewer sees the pair a rep would.
  // All draft, so none of this reaches a station team.
  {
    id: 'advertiser-onepager-v2',
    category: 'Previous version',
    title: 'One-Pager for Advertisers (previous)',
    blurb: () => 'The version this replaced, kept for comparison.',
    minRole: 'sales_rep',
    fileSuffix: 'Advertisers-v2',
    draft: true,
  },
  {
    id: 'guide-onepager-v2',
    category: 'Previous version',
    title: 'One-Pager for Getting Started (previous)',
    blurb: () => 'The version this replaced, kept for comparison.',
    minRole: 'sales_rep',
    fileSuffix: 'Getting-Started-v2',
    draft: true,
  },
  {
    id: 'rep-onepager-v2',
    category: 'Previous version',
    title: 'One-Pager for Sales Reps (previous)',
    blurb: () => 'The version this replaced, kept for comparison.',
    minRole: 'manager',
    fileSuffix: 'Reps-v2',
    draft: true,
  },
];

/**
 * The materials a caller may see, in catalogue order. An unknown or missing
 * role sees nothing rather than defaulting open — rankOf() returns 0 for
 * anything it doesn't recognise, which is below every material's floor.
 * Drafts (CTV-752) are hidden from station teams; super_admins see them
 * (the page badges them as drafts) so they can review a sheet in place.
 *
 * `catalog` exists so tests can exercise the gates against a fabricated
 * list; production callers always use the default.
 */
export function materialsForRole(
  role: string | null | undefined,
  catalog: MaterialDef[] = MATERIALS,
): MaterialDef[] {
  const caller = rankOf(role);
  const seesDrafts = role === 'super_admin';
  return catalog.filter((m) => (seesDrafts || !m.draft) && caller >= rankOf(m.minRole));
}

/**
 * Filename for a printed/downloaded material, e.g. `One-Pager_kezi_Advertisers`
 * or `Display-Ad_kezi_300x250`. No extension: the browser appends .pdf when
 * saving from the print dialog, and the PNG download adds its own.
 */
export function materialFileName(
  material: MaterialDef,
  slug: string,
  variant: MaterialVariant = {},
): string {
  // CTV-856: the email signature is its own kind, and its suffix IS the
  // kind, so it leads: `Email-Signature_kezi`, never `Display-Ad_kezi_...`.
  const signature = !!material.displayAd && DISPLAY_AD_SPECS[material.displayAd].signature;
  // CTV-863: a video is named for what it is: `Video_kezi_streamline-30s`.
  const kind = material.video ? 'Video' : material.displayAd ? 'Display-Ad' : 'One-Pager';
  const parts = signature ? [material.fileSuffix, slug] : [kind, slug, material.fileSuffix];
  // CTV-856: the signature is taken at a chosen size: `Email-Signature_kitv_400x80.png`.
  if (variant.size) parts.push(fileSlug(variant.size));
  // CTV-840: a display ad can be saved in several versions, and the file has
  // to say which: `Display-Ad_kitv_300x250_evening-anchors_light.png`.
  if (variant.version) parts.push(fileSlug(variant.version));
  if (variant.promo) parts.push(fileSlug(variant.promo));
  if (variant.look === 'light') parts.push('light');
  return parts.join('_');
}

/** The ways one display ad can be rendered, as they appear in its filename. */
export interface MaterialVariant {
  /** CTV-856: the pixel size the email signature was taken at, e.g. `400x80`. */
  size?: string | null;
  /** A saved still's name; omitted for the plain screen. */
  version?: string | null;
  /** A promotion code the ad advertises; omitted when none. */
  promo?: string | null;
  /** Omitted for dark, the default. */
  look?: 'dark' | 'light';
}

/** "Evening Anchors!" → "evening-anchors"; never empty, so the filename stays parseable. */
export function fileSlug(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'version';
}

export interface MaterialGroup {
  category: MaterialCategory;
  materials: MaterialDef[];
}

/**
 * The caller's materials, grouped for the picker. Groups appear in the order
 * their first member appears in MATERIALS, and a group with no visible
 * materials is omitted entirely rather than rendering an empty section.
 */
export function materialGroupsForRole(
  role: string | null | undefined,
  catalog: MaterialDef[] = MATERIALS,
): MaterialGroup[] {
  const groups: MaterialGroup[] = [];
  for (const m of materialsForRole(role, catalog)) {
    const existing = groups.find((g) => g.category === m.category);
    if (existing) existing.materials.push(m);
    else groups.push({ category: m.category, materials: [m] });
  }
  return groups;
}

/**
 * CTV-863: the caller's groups with the station's Videos group in place,
 * before the archive. Unlike every other group, Videos is shown EMPTY to a
 * caller who may add one, so the first commercial has somewhere to go; a
 * rep of a station with no video sees no group. Separate from
 * materialGroupsForRole because the videos are the station's, not the
 * catalogue's, and that function stays a pure read of the catalogue.
 */
export function withVideosGroup(
  groups: MaterialGroup[],
  videos: MaterialsVideo[],
  role: string | null | undefined,
): MaterialGroup[] {
  const seesVideos = rankOf(role) >= rankOf('sales_rep');
  if (!seesVideos || (videos.length === 0 && !canEditVideos(role))) return groups;
  const group: MaterialGroup = { category: 'Videos', materials: videos.map(videoMaterial) };
  const archive = groups.findIndex((g) => g.category === 'Previous version');
  const out = [...groups];
  out.splice(archive === -1 ? out.length : archive, 0, group);
  return out;
}

/**
 * The chips at a card's foot (feedback 2026-09-08): what medium it is for
 * and what opening it gives you. Derived, not stored, so a new entry cannot
 * forget them. (A "Customizable" chip for the TV sizes was tried and cut.)
 */
export function materialChips(material: MaterialDef): string[] {
  if (material.video) return ['Video', 'MP4'];
  if (!material.displayAd) return ['Print', 'PDF', 'Letter'];
  if (DISPLAY_AD_SPECS[material.displayAd].signature) return ['Email', 'PNG'];
  return [material.displayAd === '1080x1080' ? 'Social' : 'Web', 'PNG'];
}

export function materialById(id: string): MaterialDef | undefined {
  return MATERIALS.find((m) => m.id === id);
}
